import Link from 'next/link';
import Image from 'next/image';
import useRouterState from '@discover-use-router-state';
import useSuburbs from '@discover-data/stores/hooks/suburb/useSuburbs';
import { type SuburbType } from '@discover-data/gql/fragments/suburb';
import { TrackProvider } from '@/components/analytics/context/AnalyticsContext';
import { TrackLink } from '@/components/analytics/hocs/withTrackableNavLinkComponent';

type City = {
  menuTitle: string;
  slug: string;
};

type CityOtherSessionsProps = {
  city: City;
  sessionType: string;
  restaurantsBySession: string[];
  suburbs?: SuburbType[];
  variant?: 'cards' | 'links';
};

const CityOtherSessions = ({
  city,
  sessionType,
  restaurantsBySession,
  suburbs = [],
  variant = 'cards',
}: CityOtherSessionsProps) => {
  const [selectedSuburbs] = useSuburbs(suburbs);
  const suburb = selectedSuburbs[0] || null;
  const [{ suburb: suburbQueryValues }] = useRouterState(['suburb']);
  const suburbQueryParams = suburbQueryValues.filter(
    (value): value is string => typeof value === 'string'
  );
  const hasMultipleSuburbParams = suburbQueryParams.length > 1;

  // Default to 'dinner' if no sessionType (base URL)
  const currentSession = sessionType || 'dinner';

  // Simple check if sessions are available
  const sessions = restaurantsBySession || [];
  const hasBreakfastDeals = sessions.includes('breakfast');
  const hasLunchDeals = sessions.includes('lunch');
  const hasDinnerDeals = sessions.includes('dinner') || sessions.includes('lasttable');

  const sessionConfig = [
    {
      key: 'dinner',
      hasDeals: hasDinnerDeals,
      slugSuffix: '',
    },
    {
      key: 'breakfast',
      hasDeals: hasBreakfastDeals,
      slugSuffix: '/breakfast',
    },
    {
      key: 'lunch',
      hasDeals: hasLunchDeals,
      slugSuffix: '/lunch',
    },
  ];

  const sessionTypeCount = sessionConfig.filter(({ hasDeals }) => hasDeals).length;

  // If there is only one session then we are probably on it.
  if (sessionTypeCount <= 1) {
    return null;
  }

  // Use suburb title and slug if available, otherwise use city
  const displayTitle = suburb?.menuTitle || city.menuTitle;
  const baseSlug = hasMultipleSuburbParams ? city.slug : suburb?.slug || city.slug;
  const suburbQuerySuffix = hasMultipleSuburbParams
    ? `?${suburbQueryParams.map((id) => `suburb=${encodeURIComponent(id)}`).join('&')}`
    : '';

  return (
    <>
      <div className="prose">
        <h2 className={`mb-5 text-xl md:text-3xl ${variant === 'cards' ? 'mt-12' : 'mt-0'}`}>
          You might also like
        </h2>
      </div>
      <ul
        className={`mb-8 mt-0 grid list-none grid-cols-1 gap-x-8 gap-y-4 p-0 sm:mb-12 ${
          variant === 'cards' ? 'md:grid-cols-2' : 'sm:grid-cols-2 lg:grid-cols-4'
        }`}>
        {sessionConfig.map(({ key, hasDeals, slugSuffix }) => {
          if (!hasDeals || currentSession === key) return null;

          return (
            <li key={key} className="min-w-0">
              <TrackProvider
                component_name={`city other session ${variant === 'cards' ? 'card' : 'link'}`}
                link_name={`${displayTitle} ${key} deals`}>
                <TrackLink>
                  <Link
                    href={`${baseSlug}${slugSuffix}${suburbQuerySuffix}`}
                    prefetch={false}
                    className={`${
                      variant === 'cards'
                        ? 'flex h-full items-center gap-4 bg-white sm:gap-6'
                        : 'inline-block py-1'
                    } text-neutral-800 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4`}>
                    {variant === 'cards' ? (
                      <>
                        <Image
                          src={`/images/city-${key}.jpg`}
                          alt=""
                          width={100}
                          height={100}
                          className="size-[100px] shrink-0 object-cover"
                        />
                        <div className="min-w-0 py-2">
                          <h3 className="m-0 break-words text-[18px] font-bold">
                            {displayTitle} {key} deals
                          </h3>
                          <p className="mb-0 mt-1 text-xs text-gray-400">
                            Book a First Table for {key} and get 50% off the food bill
                          </p>
                        </div>
                      </>
                    ) : (
                      <>
                        {displayTitle} {key} deals
                      </>
                    )}
                  </Link>
                </TrackLink>
              </TrackProvider>
            </li>
          );
        })}
      </ul>
    </>
  );
};

export default CityOtherSessions;
